import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import '../form.css';
import { ClosedCode, PublicEditResponse, PublicFormResponse, PublicFormSettings, clearPublicPartial, enterFormPassword, getPublicEdit, getPublicForm, putPublicEdit, requestMagicLink, requestResumeLink, savePublicPartial, signOutOfForm, submitPublicForm, uploadPublicFiles, verifyPublicPayment } from '../api/public';
import { FormLogo, accentWash, normalizeRules, themeLogos } from '../shared/types';
import { FormState, PublishedDef, createInitialState, resolveInstance, stateFromData, valueKey } from '../form-engine/model';
import { evaluateForm } from '../form-engine/evaluate';
import { ValidationError, buildSubmissionData, hiddenAtSubmit, validateForm } from '../form-engine/payload';
import { STRINGS as S } from '../form-engine/strings';
import { ApiRequestError } from '../api/client';
import FormRenderer from '../form-engine/FormRenderer';
import FormMagicBadge from '../form-engine/FormMagicBadge';
import { ErrorSummaryTop, PagerControls, PagerProgress, useFormPager } from '../form-engine/FormPager';
import { clearDraft, draftKey, draftPayload, hasAnswers, loadDraft, restoreDraft, saveDraft } from '../form-engine/resume';
import { fontStack, fontsHref } from '../shared/brandFonts';
import { track } from '../form-engine/analytics';
import { CAPTCHA_PATH, CaptchaField } from '../form-engine/Turnstile';
import { isFramed, startHeightBeacon } from '../form-engine/embedResize';
import { markSent, wasSent } from '../form-engine/once';
import { applyPrefill, forwardableSearch, stripFromAddressBar, trackingParams } from '../form-engine/prefillQuery';
import { COARSE, matchesNow } from '../shared/media';

const EMPTY_DEF: PublishedDef = { fields: [], layout: { version: 1, sections: [] }, rules: [], widgets: [] };

/** The address bar's query, or '' where there is no window (tests, SSR). */
function readSearch(): string {
  try { return window.location.search || ''; } catch { return ''; }
}
const EMPTY_STATE: FormState = createInitialState(EMPTY_DEF, {});
const EMPTY_CFG = {};

/** The `?test=` token the builder's Test mode opened this page with (R9), or ''. */
function readTestToken(): string {
  try { return new URLSearchParams(window.location.search).get('test') || ''; } catch { return ''; }
}

/** The Responses table, on the Test chip, open on this test (R9). */
function responsesHref(t: { formId: number; submissionId: number }): string {
  const conditions = encodeURIComponent(JSON.stringify([{ column: 'meta:isTest', op: 'is', value: 'true' }]));
  return `/admin/forms/${t.formId}/submissions/${t.submissionId}?conditions=${conditions}`;
}

/**
 * The test-mode strip (R9, screen 24): a logo slot, what this page is, and
 * the way back. Rendered over every state of the page — the gates too — and
 * only when the page was opened with a `?test=` token, so a respondent never
 * sees it. `info` is the server's verdict on the token: good, expired, or
 * not yet known while the form loads.
 */
function TestModeBar({ info, logos }: { info: PublicFormResponse['test'] | undefined; logos: FormLogo[] }) {
  const verdict = !info ? 'pending' : 'expired' in info ? 'expired' : 'ok';
  const formId = info && !('expired' in info) ? info.formId : null;
  return (
    <div className="of-test-bar" role="region" aria-label="Test mode">
      <div className="of-test-bar-logo">
        {logos[0] ? <img src={logos[0].url} alt="" /> : <span className="of-test-bar-mark">Form-Magic</span>}
      </div>
      <div className={`of-test-bar-text${verdict === 'expired' ? ' expired' : ''}`}>
        {verdict === 'expired'
          ? 'This test link has expired · nothing you submit here will be saved'
          : 'Test mode · submissions are saved as tests'}
      </div>
      <a className="of-test-bar-back" href={formId ? `/admin/forms/${formId}/build` : '/admin'}>← Back to editor</a>
    </div>
  );
}

/** The public form at /f/:slug — rendered by the same engine as the builder's test mode. */
/** Fill {{tokens}} in thank-you copy and redirect URLs from the submission. */
function fillTokens(tpl: string, values: Record<string, unknown>): string {
  return String(tpl || '').replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (_, k: string) => {
    const v = values[k];
    return v === undefined || v === null ? '' : String(v);
  });
}

/**
 * The closed-form state, as the RESPONDENT sees it. The billing reasons
 * ('volume' / 'disabled') stay deliberately cause-neutral — same words for
 * both, and no plan, limit or price ever appears here: the customer's billing
 * state is not their visitor's business. The schedule reasons are the owner's
 * OWN closes, so those may speak: the opens-at date before the window, and
 * the owner's closedMessage after it or once the registration cap fills.
 */
function ClosedNotice({ reason, schedule, contact, inline = false }: {
  reason: ClosedCode;
  schedule?: PublicFormSettings['schedule'];
  contact: string | null;
  inline?: boolean;
}) {
  const Title = inline ? 'h2' : 'h1';
  const opensAt = reason === 'not_open_yet' && schedule?.opensAt ? new Date(schedule.opensAt) : null;
  const ownClose = reason === 'closed_schedule' || reason === 'closed_quota';
  const title = reason === 'not_open_yet'
    ? "This form isn't open yet"
    : ownClose
      ? 'This form is no longer accepting responses'
      : "This form isn't accepting responses right now";
  const body = opensAt && !Number.isNaN(opensAt.getTime())
    ? `It opens on ${opensAt.toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' })}.`
    : ownClose && schedule?.closedMessage
      ? schedule.closedMessage
      : 'Nothing you typed was lost — try again later, or contact them directly.';
  return (
    <div className={inline ? 'of-closed-inline' : 'of-closed'}>
      <Title className="of-closed-title">{title}</Title>
      <p className="of-closed-body">{body}</p>
      {contact && <div className="of-closed-contact">{contact}</div>}
    </div>
  );
}

/** One entry of the server's `errors[]` on a refused submission. */
interface ServerFieldError {
  field: string;
  label?: string;
  message?: string;
  kind?: 'required' | 'invalid';
  where?: string;
  /** the widget instance the answer sits in, root first */
  path?: { scope: string; index: number }[];
}

/**
 * The server's refusal, as the errors the renderer shows. Instance paths
 * become instance ids so an error inside "Location 2" lands on that
 * location's control; a field the client cannot place is kept for the
 * summary alone.
 */
function serverFieldErrors(err: unknown, def: PublishedDef, state: FormState): ValidationError[] {
  const body = err instanceof ApiRequestError ? (err.body as { errors?: unknown } | null) : null;
  const list = Array.isArray(body?.errors) ? (body!.errors as ServerFieldError[]) : [];
  return list
    .filter((e) => e && typeof e.field === 'string')
    .map((e) => {
      const inst = e.path?.length ? resolveInstance(state, e.path) : null;
      const base = valueKey(inst?.id ?? null, e.field);
      return {
        key: e.field,
        label: e.label || def.fields.find((f) => f.key === e.field)?.label || e.field,
        where: e.where || '',
        message: e.kind === 'required' ? undefined : (e.message || undefined),
        path: base,
      };
    });
}

/** The captcha's entry in the errors list — a control with no field behind it. */
const captchaError = (message: string): ValidationError =>
  ({ key: CAPTCHA_PATH, label: S.captcha.label, where: '', message, path: CAPTCHA_PATH });

export default function PublicFormPage() {
  const { slug = '', accountSlug } = useParams();
  /** Canonical path carries the account; the short one stays valid for old links. */
  const formPath = accountSlug ? `${accountSlug}/${slug}` : slug;
  const [resp, setResp] = useState<PublicFormResponse | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [pw, setPw] = useState('');
  const [pwError, setPwError] = useState('');
  const [state, setState] = useState<FormState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** while files go up: which one, of how many, and how far — for the overlay */
  const [uploadProgress, setUploadProgress] = useState<{ index: number; count: number; pct: number } | null>(null);
  const [result, setResult] = useState<{
    ok: boolean; submissionId?: string; error?: string;
    /** a 409: this email has responded before — the owner's message, no retry */
    duplicate?: boolean;
    /** the row was saved as a test (R9): where to see it */
    test?: { formId: number; submissionId: number };
    /** the test link had run out when Submit was pressed (R9): nothing was saved, no retry here */
    testExpired?: boolean;
    values?: Record<string, unknown>;
    /** set when this person is to carry straight on into the next form */
    continueUrl?: string;
    /** a private, short-lived link to a PDF of what they just submitted */
    documentUrl?: string;
    /** a signed link to change these answers (P6), and when it stops working */
    editUrl?: string;
    editUntil?: string;
    /** this result is a saved EDIT, not a submission (P6) */
    edited?: boolean;
    /**
     * The ending this submission's path reached, when the form has a workflow.
     *
     * Absent means "use the form's own After-submitting setting", which is
     * what every form without a workflow gets and what every form got before
     * endings existed.
     */
    completion?: PublicFormSettings['completion'];
  } | null>(null);
  /** Why a completed sign-in was still refused — set by the SSO callback. */
  const [gateError, setGateError] = useState('');
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSent, setMagicSent] = useState(false);
  const [magicBusy, setMagicBusy] = useState(false);
  /**
   * A submit that came back `closed` — the form stopped collecting while this
   * person was mid-fill. The fields stay rendered with everything they typed
   * (nothing typed was lost, literally), and the closed notice sits above the
   * form instead of replacing it.
   */
  const [submitClosed, setSubmitClosed] = useState<{ closed: ClosedCode; contact: string | null } | null>(null);
  /** Honeypot: hidden from people, filled by dumb bots; sent along at submit. */
  const [honeypot, setHoneypot] = useState('');
  /** The Turnstile token (U6) when the form asks for one; '' until solved or after it expires. */
  const [captchaToken, setCaptchaToken] = useState('');
  /** Bumped to make the widget start over after the server refused a token. */
  const [captchaReset, setCaptchaReset] = useState(0);
  /**
   * The last submit was refused for its token. While true the message stays
   * up until the next submit: the widget starts over on its own, and most
   * people never touch it, so a token arriving is not the person's doing and
   * must not quietly clear what the server just said.
   */
  const captchaRefused = useRef(false);
  // Framed on somebody else's page? Tell it how tall we are (U6). A no-op at top level.
  useEffect(() => startHeightBeacon(window, document, { selector: '.of-page' }), []);
  /** A saved in-progress draft found in localStorage, awaiting the person's choice. */
  const [resumeOffer, setResumeOffer] = useState<{ state: FormState; page: number; savedAt: number } | null>(null);
  /**
   * Editing after submit (P6): the page opened on an ?edit= token. The stored
   * answers seed the form, the locked ones stay locked, and Submit becomes
   * "Save changes" — everything else about the page is the form as it is.
   */
  const [edit, setEdit] = useState<{
    token: string; submissionId: string; data: Record<string, unknown>;
    provided: Record<string, string>; lockedFields: string[]; expiresAt: string;
  } | null>(null);
  /** The token was refused: expired, forged, or the owner switched editing off. */
  const [editFailed, setEditFailed] = useState(false);
  /** "Save and continue later" (P6): the email dialog, and what happened. */
  const [later, setLater] = useState<{ email: string; busy: boolean; sent?: string; error?: string } | null>(null);
  /**
   * Has the PERSON changed anything — as opposed to the page filling values in
   * for them?
   *
   * `state.touched` cannot answer this: prefill and carried answers are applied
   * through `setValue`, which marks them touched exactly as typing does. Gating
   * the autosave on that meant a prefilled form saved an almost-empty draft the
   * instant it loaded, overwriting the real one and dismissing the offer to
   * restore it before anybody saw it.
   */
  const [dirty, setDirty] = useState(false);
  /**
   * The query the form was opened with (P1), read once: tracking params
   * (`utm_*`, `ref`, …) ride on the submission as `meta.query`. Captured
   * before the effects above clean anything out of the address bar.
   */
  const arrival = useRef(readSearch());
  /**
   * Test mode (R9, D11): the page was opened from the builder with a signed
   * token. It stays in the address bar on purpose — a reload must not turn a
   * test into a real response — and travels on the submit, where the server
   * checks it. While it is set: no analytics beacons, no once-per-browser
   * mark, the strip above the form, and "See it in Responses" at the end.
   */
  const testToken = useRef(readTestToken());
  const testMode = !!testToken.current;
  /** Verdicts on carried-over answers (the order handoff), and whether to show what is missing. */
  const [confirmations, setConfirmations] = useState<Record<string, boolean>>({});
  const [showConfirmErrors, setShowConfirmErrors] = useState(false);

  useEffect(() => {
    try {
      const err = new URLSearchParams(window.location.search).get('sso_error');
      if (err) { setGateError(err); stripFromAddressBar(['sso_error']); }
    } catch { /* ignore */ }
  }, []);

  // The invite token is spent into the session by the GET above, so it can come
  // out of the address bar. A link somebody copies from there to paste into a
  // chat should not still be a working credential.
  useEffect(() => {
    if (!resp || resp.locked) return;
    try {
      const q = new URLSearchParams(window.location.search);
      // Only the token goes: a prefill link's own params stay, so a refresh fills the same way.
      if (q.get('invite')) stripFromAddressBar(['invite']);
      // A resume link (P6) is held in the session the same way.
      if (q.get('resume')) stripFromAddressBar(['resume']);
    } catch { /* ignore */ }
  }, [resp]);

  // Stripe Checkout return: ?paid_session confirms with the server (which
  // marks the submission paid), ?payment=canceled explains what happened.
  const [payReturn, setPayReturn] = useState<'verifying' | 'paid' | 'unpaid' | 'canceled' | null>(null);
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const session = q.get('paid_session');
      const canceled = q.get('payment') === 'canceled';
      if (!session && !canceled) return;
      stripFromAddressBar(['paid_session', 'payment']);
      if (canceled) { setPayReturn('canceled'); return; }
      setPayReturn('verifying');
      verifyPublicPayment(formPath, String(session))
        .then((r) => setPayReturn(r.paid ? 'paid' : 'unpaid'))
        .catch(() => setPayReturn('unpaid'));
    } catch { /* ignore */ }
  }, [formPath]);

  const def = useMemo<PublishedDef | null>(() => (
    resp && !resp.locked && resp.fields && resp.layout
      ? {
        fields: resp.fields,
        layout: resp.layout,
        rules: normalizeRules(resp.rules),
        widgets: resp.widgets || [],
        // The order this form continues, for the rules that name it. Absent
        // on a form that continues nothing — see PublishedDef.parent.
        ...(resp.parent ? { parent: resp.parent } : {}),
      }
      : null
  ), [resp]);

  const load = useCallback(() => {
    // Read straight from the address bar rather than from state: this runs on
    // the first render, before anything has had a chance to store it.
    // The rest of the query goes along too (P1): the prefill params, and a
    // signed link's `t` and `sig`, which the server resolves on this GET.
    let invite: string | undefined;
    let search = '';
    let editToken = '';
    try {
      const s = window.location.search;
      const q = new URLSearchParams(s);
      invite = q.get('invite') || undefined;
      editToken = q.get('edit') || '';
      search = forwardableSearch(s);
      // A resume link (P6) travels like the invite: forwarded on this GET,
      // where the server remembers it, then cleaned out of the address bar.
      const resume = q.get('resume');
      if (resume) search = `${search ? `${search}&` : ''}resume=${encodeURIComponent(resume)}`;
    } catch { /* ignore */ }
    // Editing after submit (P6): the token is the credential and the route
    // answers with the definition plus the stored answers — no gate, no
    // draft, no prefill; the answers ARE the prefill.
    if (editToken) {
      getPublicEdit(formPath, editToken)
        .then((r: PublicEditResponse) => {
          setEdit({
            token: editToken, submissionId: r.submissionId, data: r.data,
            provided: r.provided || {}, lockedFields: r.lockedFields || [], expiresAt: r.expiresAt,
          });
          setResp({
            success: true, form: r.form, passwordRequired: false, locked: false,
            advertisement: (r as unknown as { advertisement?: boolean }).advertisement,
            fields: r.fields, layout: r.layout, rules: r.rules, widgets: r.widgets,
            // The order behind this submission, so a `parent.*` rule weighs
            // the same answer it did when the form was filled in.
            ...(r.parent ? { parent: r.parent } : {}),
          });
          document.title = r.form.name;
        })
        .catch((err: unknown) => {
          if ((err as ApiRequestError)?.status === 404 && (err as ApiRequestError)?.message !== 'Form not found') {
            // The form is there; the link is not. Fall through to the plain
            // form with a note, rather than a dead end.
            setEditFailed(true);
            stripFromAddressBar(['edit']);
            getPublicForm(formPath, invite, search)
              .then((r) => { setResp(r); document.title = r.form.name; })
              .catch(() => setNotFound(true));
          } else {
            setNotFound(true);
          }
        });
      return;
    }
    getPublicForm(formPath, invite, search)
      .then((r) => {
        setResp(r);
        document.title = r.form.name;
      })
      .catch(() => setNotFound(true));
  }, [formPath]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!def) return;
    // Editing after submit (P6): the stored answers are the whole seed, and
    // no draft is offered over them — a draft is for a form not yet sent.
    if (edit) {
      setState(stateFromData(def, edit.data));
      setDirty(false);
      setResumeOffer(null);
      return;
    }
    // Values taken from the signed-in account, the order this continues or
    // the link start filled in. The server re-applies the locked ones at
    // submit, so this is presentation, not enforcement — these are
    // order-scope answers, set the same way a person typing would.
    const next = applyPrefill(createInitialState(def, {}), resp?.prefill);
    setState(next);
    setDirty(false);
    // An autosaved draft is OFFERED, never silently restored — the person may
    // be back to start something new on purpose.
    //
    // The server's copy wins when there is one: it follows the person rather
    // than the browser, so it is the newer one whenever they have changed
    // device — which is the entire reason it exists.
    const fromServer = resp?.partial
      ? restoreDraft(resp.partial.data, def, Date.parse(resp.partial.savedAt))
      : null;
    setResumeOffer(fromServer || loadDraft(draftKey(formPath), def));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def, resp?.prefill, resp?.partial, edit]);

  // Hook order requires this before any early return; on gate/closed screens
  // it just runs over the empty definition.
  // D15: the form's own switch decides where the summary of what to fix goes.
  const pager = useFormPager(def ?? EMPTY_DEF, state ?? EMPTY_STATE, EMPTY_CFG, { errorSummary: resp?.form.settings.errorSummary === true });

  // Analytics: a view once the definition is actually visible (a gated form
  // counts after its gate, a closed one never), a page_view per pager page,
  // a start on the first typed answer. All fire-and-forget beacons.
  const startTracked = useRef(false);
  // A test fill (R9) sends none of them: Analytics counts respondents.
  useEffect(() => {
    if (def && !testMode) track(formPath, 'view');
  }, [def, formPath, testMode]);
  useEffect(() => {
    if (def && !testMode) track(formPath, 'page_view', pager.index);
  }, [def, formPath, pager.index, testMode]);
  useEffect(() => {
    if (def && state && hasAnswers(state) && !startTracked.current && !testMode) {
      startTracked.current = true;
      track(formPath, 'start');
    }
  }, [def, state, formPath, testMode]);

  // Autosave: debounced to localStorage once the person has typed anything.
  // Typing also stands as the answer to a pending "Resume?" offer — someone
  // filling in fresh answers has chosen not to resume.
  useEffect(() => {
    if (!def || !state || !dirty || !hasAnswers(state) || edit) return;
    setResumeOffer((offer) => (offer ? null : offer));
    const t = setTimeout(() => {
      saveDraft(draftKey(formPath), def, state, pager.index);
      // …and server-side when this form has an invite, so the draft survives a
      // change of device. Fire-and-forget; a 204 comes back for forms without
      // one and nothing here has to know the difference.
      // …and to the resume link's draft (P6) once one has been asked for.
      if (resp?.gate === 'invite' || resp?.carried || later?.sent) {
        void savePublicPartial(formPath, draftPayload(def, state, pager.index));
      }
    }, 800);
    return () => clearTimeout(t);
  }, [def, state, dirty, pager.index, formPath, resp?.gate, resp?.carried, later?.sent, edit]);

  function onResume() {
    if (!resumeOffer || !def) return;
    // Locked identity prefill wins over whatever the draft remembered — but an
    // UNSIGNED link's values do not: somebody back to a saved draft keeps what
    // they typed rather than having the link's value stamped over it (P1).
    const restored = applyPrefill(resumeOffer.state, resp?.prefill, new Set(resp?.softPrefill || []));
    setState(restored);
    setDirty(false);
    pager.goTo(resumeOffer.page);
    setResumeOffer(null);
  }

  function onStartOver() {
    clearDraft(draftKey(formPath));
    void clearPublicPartial(formPath);
    setResumeOffer(null);
  }

  const theme = resp?.form.settings.theme || {};
  // Branding: curated Google Fonts load through one <link>; dark mode is a
  // class on the page ('auto' follows the visitor's OS via CSS).
  const bodyStack = fontStack(theme.font?.body);
  const headingStack = fontStack(theme.font?.heading || theme.font?.body);
  const pageStyle = {
    '--of-accent': theme.accent || '#667eea',
    // The page behind the form and the submit button are drawn accent ->
    // accent2. A theme that sets only `accent` is flat, which is why the
    // fallback is the accent itself and not the stock purple.
    ...(theme.accent || theme.accent2 ? { '--of-accent2': theme.accent2 || theme.accent } : {}),
    // And the whole band, once a theme has anything to say about it: the
    // direction lives in the value, not in the two stops.
    ...(theme.accent || theme.accent2 ? { '--of-accent-wash': accentWash(theme) } : {}),
    ...(theme.maxWidth ? { '--of-max-width': `${theme.maxWidth}px` } : {}),
    ...(theme.bg ? { background: theme.bg } : {}),
    ...(bodyStack ? { '--of-font-body': bodyStack } : {}),
    ...(headingStack ? { '--of-font-heading': headingStack } : {}),
  } as React.CSSProperties;
  // Framed on another page, the form is content, not a screen: no viewport
  // minimum, so the height it reports is its own.
  const pageClass = `of-page${theme.mode === 'dark' ? ' of-dark' : theme.mode === 'auto' ? ' of-auto' : ''}${isFramed() ? ' of-framed' : ''}`;

  useEffect(() => {
    const href = fontsHref(theme.font);
    if (!href) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, [theme.font?.body, theme.font?.heading]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * The logo row: one cell per position, so a carrier's logo on the left and a
   * partner's on the right share a line. Each logo is placed by its own column
   * class rather than by its order in the list — the grid holds all three
   * columns open, so a lone centre logo lands exactly where the one logo a
   * form used to have landed.
   */
  const logos = themeLogos(theme);
  const logo = logos.length ? (
    <div className={`of-logo${logos.length > 1 ? ' of-logo-multi' : ''}`}>
      {logos.map((l) => (
        // The height travels as a custom property rather than an inline
        // `height`, so the phone rule for a row of two or three can trade it
        // for a max-height and let the logos share the width. Inline, it would
        // win against the stylesheet and they would squeeze each other out.
        <div
          key={l.url} className={`of-logo-${l.align}`}
          style={{ ['--of-logo-h' as string]: `${Math.min(400, l.height || 60)}px` }}
        >
          <img src={l.url} alt="" />
        </div>
      ))}
    </div>
  ) : null;

  async function onPassword(e: React.FormEvent) {
    e.preventDefault();
    setPwError('');
    try {
      await enterFormPassword(formPath, pw);
      setPw('');
      load();
    } catch (err: any) {
      setPwError(err.message || 'Incorrect password');
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!def || !state) return;
    const ev = evaluateForm(def, state, {});
    // Pages a goto jumped over are neither checked nor sent.
    const errors = validateForm(def, state, ev, {}, pager.skippedSections);
    if (errors.length) {
      pager.showErrors(errors);
      return;
    }
    // Carried answers awaiting a verdict. Checked again server-side — this is
    // so the person is told which ones, next to the answers themselves.
    const unconfirmed = (resp?.confirmFields || []).filter((k) => typeof confirmations[k] !== 'boolean');
    if (unconfirmed.length) {
      setShowConfirmErrors(true);
      const label = (k: string) => def.fields.find((f) => f.key === k)?.label || k;
      pager.showErrors([], `Please confirm whether these details are correct: ${unconfirmed.map(label).join(', ')}.`);
      return;
    }
    // The captcha is a control like any other: unsolved, it is the error the
    // summary names and focuses, and nothing is sent.
    if (resp?.captcha && !captchaToken) {
      pager.showErrors([captchaError(S.captcha.required)]);
      return;
    }
    captchaRefused.current = false;
    pager.clearErrors();
    setSubmitting(true);
    try {
      const data = buildSubmissionData(def, state, ev, pager.skippedSections);
      // What was not on their screen as they sent it — the document's list of
      // answers leaves these off. Same config as the validation above.
      const hidden = hiddenAtSubmit(def, state, ev, {}, pager.skippedSections);
      // Upload picked files and merge their URLs under the file field keys.
      const fileFields = def.fields.filter((f) => f.input_type === 'file');
      // Order-scope file fields only (file fields inside widget instances upload under their instance key).
      const batches = fileFields
        .map((f) => ({ f, picked: state.files[valueKey(null, f.key)] || [] }))
        .filter((b) => b.picked.length);
      const count = batches.reduce((n, b) => n + b.picked.length, 0);
      let done = 0;
      for (const { f, picked } of batches) {
        // One request per field, in turn: the overlay counts files, not
        // requests, so "3 of 5" means what it says.
        const batchBytes = picked.reduce((n, p) => n + p.size, 0) || 1;
        setUploadProgress({ index: done + 1, count, pct: 0 });
        const uploaded = await uploadPublicFiles(formPath, f.key, picked, (loaded, total) => {
          const frac = Math.min(1, loaded / (total || batchBytes));
          const inBatch = Math.min(picked.length - 1, Math.floor(frac * picked.length));
          setUploadProgress({ index: done + inBatch + 1, count, pct: Math.round(frac * 100) });
        });
        done += picked.length;
        data[f.key] = uploaded.map((u) => u.url);
      }
      setUploadProgress(null);
      // How each signature was made. Keyed by FIELD, because the record is a
      // fact about the signing and not about one repeated instance of it — a
      // form signed once inside a widget signed once.
      const signatureModes: Record<string, { mode: 'draw' | 'type'; typedName?: string }> = {};
      for (const [k, m] of Object.entries(state.signatures)) {
        const fieldKey = k.includes('.') ? k.slice(k.indexOf('.') + 1) : k;
        if (m) signatureModes[fieldKey] = m;
      }
      // Editing after submit (P6): the same payload goes to the edit route,
      // which keeps what the person did not replace and writes a revision.
      if (edit) {
        const saved = await putPublicEdit(formPath, edit.token, data, signatureModes, hidden);
        setResult({ ok: true, submissionId: saved.submissionId, edited: true, values: { ...data, submissionId: saved.submissionId } });
        return;
      }
      const r = await submitPublicForm(
        formPath, data, honeypot, confirmations, signatureModes, captchaToken || undefined, trackingParams(arrival.current),
        testToken.current || undefined, hidden
      );
      // A submission that owes money continues on Stripe's page; the return
      // URL brings the person back here with the outcome.
      if (r.payment?.checkoutUrl) {
        // The submission exists before Checkout; the draft's job is done.
        clearDraft(draftKey(formPath));
        if (!testMode) track(formPath, 'submit'); // sendBeacon survives the navigation
        window.location.href = r.payment.checkoutUrl;
        return;
      }
      if (r.closed) {
        // The respondent exception: a 200 with a closed state, never an error.
        // Keep the page exactly as it is — fields, answers and all.
        setSubmitClosed({ closed: r.closed, contact: r.contact ?? null });
        return;
      }
      clearDraft(draftKey(formPath));
      if (!testMode) track(formPath, 'submit');
      // Once per browser (U6): remembered here, at the same "this browser is
      // done" moment the draft is cleared. A test (R9) leaves the browser
      // free to test again.
      if (resp?.oncePerBrowser && !testMode) markSent(formPath);
      // The server's results join the answers, so a thank-you page can say
      // {{inviteUrl}} or redirect to {{continueUrl}} the same way it says
      // {{companyName}}.
      setResult({
        ok: true,
        submissionId: r.submissionId,
        values: { ...data, ...(r.tokens || {}), ...(r.continueUrl ? { continueUrl: r.continueUrl } : {}) },
        continueUrl: r.continueUrl,
        documentUrl: r.documentUrl,
        editUrl: r.editUrl,
        editUntil: r.editUntil,
        completion: r.completion,
        test: r.test,
      });
    } catch (err: any) {
      const body = (err as ApiRequestError)?.body as { captcha?: string; duplicate?: boolean; testExpired?: boolean } | undefined;
      // The test link ran out (R9): nothing was saved, and trying again here
      // would not help — the way on is back through the editor.
      if (body?.testExpired) {
        setResult({ ok: false, testExpired: true, error: err.message || 'This test link has expired.' });
        return;
      }
      // A refused token: the widget starts over and the summary says so —
      // the answers stay exactly as typed.
      if (body?.captcha) {
        captchaRefused.current = true;
        setCaptchaToken('');
        setCaptchaReset((n) => n + 1);
        pager.showErrors([captchaError(err.message || S.captcha.required)]);
        return;
      }
      // This email has responded before (U6): the owner's message, no retry.
      if (body?.duplicate) {
        setResult({ ok: false, duplicate: true, error: err.message || S.duplicate.body });
        return;
      }
      // A refusal that names fields lands on those fields, exactly as the
      // client's own check would have — a rule the client missed, a date
      // bound that crossed midnight. Anything else is the error screen.
      const mapped = serverFieldErrors(err, def, state);
      if (mapped.length) pager.showErrors(mapped);
      else setResult({ ok: false, error: err.message || 'Submission failed' });
    } finally {
      setSubmitting(false);
      setUploadProgress(null);
    }
  }

  function resetForm() {
    if (def) setState(createInitialState(def, {}));
    setResult(null);
    pager.reset();
  }

  /** "Save and continue later" (P6): open the dialog with the best guess at their address. */
  function openLater() {
    if (!def || !state) return;
    const emailField = def.fields.find((f) => f.input_type === 'email' && f.scope === 'order');
    const guess = emailField ? String(state.order[emailField.key] || '') : '';
    setLater({ email: guess, busy: false });
  }

  async function sendLater(e: React.FormEvent) {
    e.preventDefault();
    if (!later || !def || !state) return;
    const email = later.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setLater({ ...later, error: S.resume.invalid }); return; }
    if (!hasAnswers(state)) { setLater({ ...later, error: S.resume.nothingYet }); return; }
    setLater({ ...later, busy: true, error: undefined });
    try {
      await requestResumeLink(formPath, email, draftPayload(def, state, pager.index));
      setLater({ email, busy: false, sent: email });
    } catch (err: any) {
      setLater({ ...later, busy: false, error: err.message || 'The link could not be sent.' });
    }
  }

  // A form set to redirect sends the respondent on after an optional pause.
  // The ending this path reached wins over the form's one setting. A form
  // with no workflow never sends one, so this is exactly what it always was.
  const completion = result?.completion || resp?.form.settings.completion || {};
  // Where a redirect ending sends them — http(s) only, never another scheme.
  const redirectTarget = (() => {
    if (!result?.ok || completion.mode !== 'redirect' || !completion.redirectUrl) return null;
    const url = fillTokens(completion.redirectUrl, { ...(result.values || {}), submissionId: result.submissionId });
    const target = !completion.includeSubmissionId || !result.submissionId
      ? url
      : url + (url.includes('?') ? '&' : '?') + `submissionId=${encodeURIComponent(result.submissionId)}`;
    return /^https?:\/\//i.test(target) ? target : null;
  })();
  // A document on offer holds the redirect: the link would otherwise be on
  // screen for the length of the delay, which defaults to nothing at all, and
  // a person who has just signed a contract is owed the chance to keep a
  // copy. They go on when they choose, by the button that takes the
  // redirect's place below.
  const holdForDocument = !!result?.documentUrl;
  useEffect(() => {
    if (!redirectTarget || holdForDocument) return;
    const t = setTimeout(() => { window.location.href = redirectTarget; }, Math.max(0, Number(completion.redirectDelay ?? 0)) * 1000);
    return () => clearTimeout(t);
  }, [redirectTarget, holdForDocument, completion.redirectDelay]);
  // An ending that continues straight on (2026-09-27): no thank-you page, the
  // next form opens as soon as the link is back. Held while a PDF is on offer,
  // as a redirect is and for the same reason — and never without a link to go
  // to, which leaves the page exactly as it always was.
  const straightOn = result?.ok && !result.edited && result.continueUrl && completion.continueStraight && !holdForDocument
    ? result.continueUrl : null;
  useEffect(() => {
    if (straightOn) window.location.href = straightOn;
  }, [straightOn]);

  /**
   * The Form-Magic badge, or nothing. The server has already weighed the
   * owner's per-form setting against their plan, so this is one boolean and
   * no plan is named on either side of the wire. A payload from before the
   * badge existed has no key at all, which reads as "show it".
   */
  const badge = resp?.advertisement === false ? null : <FormMagicBadge />;
  /** The same badge over a full-screen gate, whose backdrop is opaque. */
  const gateBadge = resp?.advertisement === false ? null : <FormMagicBadge floating />;
  /**
   * Every state of the page goes through here: in test mode (R9) the strip
   * sits over it; otherwise the page is returned exactly as built, so a
   * respondent's DOM is what it always was.
   */
  const shell = (page: JSX.Element): JSX.Element => (testMode
    ? <><TestModeBar info={resp?.test} logos={logos} />{page}</>
    : page);

  if (notFound) {
    return shell(
      <div className={pageClass} style={pageStyle}>
        <div className="of-modal"><div className="of-modal-card">
          <h2>Form not found</h2>
          <p>This form does not exist or is no longer available.</p>
        </div></div>
      </div>
    );
  }
  if (!resp) {
    return shell(<div className={pageClass} style={pageStyle}><div className="of-overlay"><div className="of-spinner" /><div>Loading…</div></div></div>);
  }

  // ---- closed form (not collecting) ----
  // Before every gate: nobody should have to sign in to learn the form is
  // closed. Both closed values render the same cause-neutral screen.
  if (resp.closed) {
    return shell(
      <div className={pageClass} style={pageStyle}>
        <ClosedNotice reason={resp.closed} schedule={resp.form.settings.schedule} contact={resp.contact ?? null} />
        {badge}
      </div>
    );
  }

  // ---- once per browser (U6) ----
  // Soft, and honoured before any gate for the same reason the closed screen
  // is: nobody should sign in to be told they are done. The thank-you
  // overlay (result) wins while it is up.
  if (resp.oncePerBrowser && !result && !testMode && wasSent(formPath)) {
    return shell(
      <div className={pageClass} style={pageStyle}>
        <div className="of-closed">
          <h1 className="of-closed-title">{S.duplicate.title}</h1>
          <p className="of-closed-body">{resp.oncePerBrowser.message || S.duplicate.body}</p>
        </div>
        {badge}
      </div>
    );
  }

  // ---- email magic-link gate ----
  if (resp.locked && resp.gate === 'magic_link') {
    return shell(
      <div className={pageClass} style={pageStyle}>
        <div className="of-container">
          {logo}
          <div className="of-header">
            <div>
              <h1>{theme.headerTitle || resp.form.name}</h1>
              {theme.headerSubtitle && <p className="of-subtitle">{theme.headerSubtitle}</p>}
            </div>
          </div>
          <div className="of-gate">
            <span className="of-gate-pill">Email check required</span>
            {magicSent ? (
              <>
                <h2>Check your email</h2>
                <p>
                  If <b>{magicEmail}</b> can open this form, a link is on its way. It expires in{' '}
                  {resp.ttlMinutes || 30} minutes.
                </p>
                <button type="button" className="fc-act" onClick={() => { setMagicSent(false); setMagicEmail(''); }}>
                  Use a different address
                </button>
              </>
            ) : (
              <>
                <h2>Confirm your email to open this form</h2>
                <p>Enter your email address and we'll send you a short-lived link. No account or password needed.</p>
                {resp.gateError
                  ? <div className="of-gate-error">{resp.gateError}</div>
                  : (
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        setMagicBusy(true);
                        try { await requestMagicLink(formPath, magicEmail.trim()); setMagicSent(true); }
                        catch (err: any) { setGateError(err.message || 'Could not send the link.'); }
                        finally { setMagicBusy(false); }
                      }}
                    >
                      <input
                        type="email" required value={magicEmail} placeholder="you@company.com"
                        className="of-gate-input" autoComplete="email" inputMode="email"
                        // Autofocusing this raises the keyboard the moment the
                        // page opens, which on a phone hides the button it is
                        // asking to be pressed (M2/C1).
                        autoFocus={!matchesNow(COARSE)}
                        onChange={(e) => setMagicEmail(e.target.value)}
                      />
                      <button type="submit" className="of-btn" disabled={magicBusy || !magicEmail.trim()} style={{ marginTop: 12 }}>
                        {magicBusy ? 'Sending…' : 'Email me a link'}
                      </button>
                    </form>
                  )}
              </>
            )}
          </div>
          {gateError && <div className="of-gate-error">{gateError}</div>}
        </div>
        {badge}
      </div>
    );
  }

  // ---- single sign-on gate ----
  // Rendered INSIDE the form container: no fields were sent, so there is
  // nothing behind it to hide.
  if (resp.locked && resp.gate === 'sso') {
    return shell(
      <div className={pageClass} style={pageStyle}>
        <div className="of-container">
          {logo}
          <div className="of-header">
            <div>
              <h1>{theme.headerTitle || resp.form.name}</h1>
              {theme.headerSubtitle && <p className="of-subtitle">{theme.headerSubtitle}</p>}
            </div>
          </div>
          <div className="of-gate">
            <span className="of-gate-pill">Sign-in required</span>
            <h2>Only people with an approved account can submit this form</h2>
            <p>
              Sign in with your {resp.provider?.name || 'company'} account. Your name and email come across automatically and are
              recorded with the submission.
            </p>
            {resp.gateError
              ? <div className="of-gate-error">{resp.gateError}</div>
              : (
                <a className="of-btn" style={{ display: 'block', textDecoration: 'none', boxSizing: 'border-box' }} href={resp.provider?.loginUrl}>
                  {resp.buttonLabel || `Continue with ${resp.provider?.name || 'SSO'}`}
                </a>
              )}
          </div>
          {gateError && <div className="of-gate-error">{gateError}</div>}
        </div>
        {badge}
      </div>
    );
  }

  // ---- invite gate ----
  // No door to knock on: an invite is addressed to one order, so there is
  // nothing a person without one could usefully be offered here.
  if (resp.locked && resp.gate === 'invite') {
    return shell(
      <div className={pageClass} style={pageStyle}>
        <div className="of-modal">
          <div className="of-modal-card">
            <h2>{resp.form.name}</h2>
            <p>
              {resp.gateError
                || 'This link has expired, was already used, or is not valid. Please ask whoever sent it to you for a new one.'}
            </p>
          </div>
        </div>
        {gateBadge}
      </div>
    );
  }

  // ---- password gate ----
  if (resp.locked) {
    return shell(
      <div className={pageClass} style={pageStyle}>
        <div className="of-modal">
          <div className="of-modal-card">
            <h2>🔐 {resp.form.name}</h2>
            <p>This form is password protected.</p>
            <form onSubmit={onPassword}>
              <input type="password" value={pw} autoFocus placeholder="Enter password" onChange={(e) => setPw(e.target.value)} />
              <button type="submit" className="of-btn" style={{ marginTop: 0 }}>Open Form</button>
            </form>
            {pwError && <p className="of-error">{pwError}</p>}
          </div>
        </div>
        {gateBadge}
      </div>
    );
  }

  if (resp.form.status === 'archived') {
    return shell(
      <div className={pageClass} style={pageStyle}>
        <div className="of-modal"><div className="of-modal-card">
          <h2>{resp.form.name}</h2>
          <p>This form is closed and no longer accepting submissions.</p>
        </div></div>
        {gateBadge}
      </div>
    );
  }

  if (!def || !state) return null;
  const settings = resp.form.settings;
  // Locks from the account or the carried order render as "from your account";
  // a signed link's locks are the link's own and go through `link` below.
  const accountLocked = (resp.lockedFields || []).filter((k) => !(resp.linkLocked || []).includes(k));

  return shell(
    <div className={pageClass} style={pageStyle}>
      {submitting && (
        <div className="of-overlay" role="status" aria-live="polite">
          <div className="of-spinner" />
          <div>{uploadProgress ? S.files.uploading(uploadProgress.index, uploadProgress.count, uploadProgress.pct) : S.files.submitting}</div>
          {uploadProgress && (
            <div className="of-upload-progress">
              <div className="of-pager-track"><div className="of-pager-fill" style={{ width: `${uploadProgress.pct}%` }} /></div>
            </div>
          )}
        </div>
      )}
      {payReturn && (
        <div className="of-modal">
          <div className="of-modal-card">
            {payReturn === 'verifying' ? (
              <><div className="of-spinner" /><p>Confirming your payment…</p></>
            ) : payReturn === 'paid' ? (
              <>
                <div className="of-result-icon">✅</div>
                <h2>Payment received</h2>
                <p>Thank you — your submission is complete and paid.</p>
                <button className="of-btn" onClick={() => setPayReturn(null)}>Done</button>
              </>
            ) : payReturn === 'canceled' ? (
              <>
                <div className="of-result-icon">⚠️</div>
                <h2>Payment not completed</h2>
                <p>Your answers were recorded, but the payment was cancelled before it went through. Contact the form owner if you need to arrange payment.</p>
                <button className="of-btn" onClick={() => setPayReturn(null)}>Close</button>
              </>
            ) : (
              <>
                <div className="of-result-icon">⚠️</div>
                <h2>Payment not confirmed yet</h2>
                <p>We couldn't confirm the payment just now. If you completed it, it will be recorded shortly — you don't need to do anything else.</p>
                <button className="of-btn" onClick={() => setPayReturn(null)}>Close</button>
              </>
            )}
          </div>
        </div>
      )}
      {later && (
        <div className="of-modal" role="dialog" aria-labelledby="pf-later-title">
          <div className="of-modal-card">
            {later.sent ? (
              <>
                <div className="of-result-icon">📬</div>
                <h2 id="pf-later-title">{S.resume.title}</h2>
                <p>{S.resume.sent(later.sent)}</p>
                <button type="button" className="of-btn" onClick={() => setLater(null)}>OK</button>
              </>
            ) : (
              <form onSubmit={sendLater}>
                <h2 id="pf-later-title">{S.resume.title}</h2>
                <p>{S.resume.body}</p>
                <label htmlFor="pf-later-email" className="of-sr-only" style={{ position: 'absolute', left: -9999 }}>{S.resume.email}</label>
                <input
                  id="pf-later-email" type="email" autoFocus placeholder={S.resume.email}
                  value={later.email} onChange={(e) => setLater({ ...later, email: e.target.value, error: undefined })}
                />
                {later.error && <p className="of-error" style={{ marginTop: -8 }}>{later.error}</p>}
                <div className="of-modal-actions">
                  <button type="button" className="of-btn of-btn-quiet" onClick={() => setLater(null)}>{S.resume.cancel}</button>
                  <button type="submit" className="of-btn" disabled={later.busy}>{S.resume.send}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      {/* Straight on into the next form: what shows while it opens, in place
          of a thank-you page nobody asked for. The submit overlay's own look. */}
      {straightOn && (
        <div className="of-overlay" role="status" aria-live="polite">
          <div className="of-spinner" />
          <div>{S.continuing}</div>
        </div>
      )}
      {result && !straightOn && (
        <div className="of-modal">
          <div className="of-modal-card">
            <div className="of-result-icon">{result.ok ? '✅' : result.duplicate ? '✋' : '❌'}</div>
            {result.ok && result.edited ? (
              <>
                <h2>{S.edit.savedTitle}</h2>
                <p>{S.edit.saved(result.submissionId || '')}</p>
                <button className="of-btn" onClick={() => setResult(null)}>{S.edit.editAgain}</button>
              </>
            ) : result.ok ? (
              <>
                <h2>{fillTokens(completion.title || S.thankYou, result.values || {})}</h2>
                <p style={{ whiteSpace: 'pre-wrap' }}>
                  {fillTokens(
                    completion.body || settings.successMessage || S.received(result.submissionId || ''),
                    { ...(result.values || {}), submissionId: result.submissionId }
                  )}
                </p>
                {/* A test (R9) runs no workflow, so a redirect whose address is
                    a delivery's token has nowhere to go: say so only when it will. */}
                {completion.mode === 'redirect' && completion.redirectUrl && (!result.test || redirectTarget) && (
                  <p className="of-info">Taking you to the next page…</p>
                )}
                {/* The rep said they would enter the details themselves, so the
                    link came back here instead of going to the customer. A
                    button rather than only an auto-redirect: they may want to
                    note the order number before carrying on, and an automatic
                    jump they interrupted would strand them. */}
                {result.continueUrl && (
                  <p style={{ marginTop: 4 }}>
                    <a className="of-btn" href={result.continueUrl} style={{ textDecoration: 'none' }}>
                      {completion.continueLabel
                        ? fillTokens(completion.continueLabel, { ...(result.values || {}), submissionId: result.submissionId })
                        : S.continueButton}
                    </a>
                  </p>
                )}
                {/*
                  Their own copy. A plain link rather than a scripted save: the
                  server sends Content-Disposition, so the browser does the
                  right thing, and a link survives being opened in a new tab or
                  copied — which is what somebody keeping a signed contract
                  actually does with it.
                */}
                {result.documentUrl && (
                  <p style={{ marginTop: 4 }}>
                    <a
                      className="of-btn of-btn-quiet" href={result.documentUrl}
                      style={{ textDecoration: 'none' }}
                    >
                      Download a PDF copy
                    </a>
                    {redirectTarget && holdForDocument && (
                      <a className="of-btn of-btn-next" href={redirectTarget} style={{ textDecoration: 'none' }}>
                        Continue
                      </a>
                    )}
                  </p>
                )}
                {/* Their way back in (P6): the link opens this page on the
                    stored answers, and works until the owner's window closes. */}
                {result.editUrl && (
                  <p style={{ marginTop: 4 }}>
                    <a className="of-btn of-btn-quiet" href={result.editUrl} style={{ textDecoration: 'none' }}>
                      {S.edit.link}
                    </a>
                    {result.editUntil && (
                      <span className="of-edit-note" style={{ display: 'block' }}>
                        {S.edit.until(new Date(result.editUntil).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }))}
                      </span>
                    )}
                  </p>
                )}
                {/* A test (R9): the row is in Responses under the Test chip. */}
                {result.test && (
                  <p style={{ marginTop: 4 }}>
                    <a className="of-btn of-btn-quiet" href={responsesHref(result.test)} style={{ textDecoration: 'none' }}>
                      See it in Responses →
                    </a>
                  </p>
                )}
                {/* An ending that goes straight on only shows this page while
                    a PDF holds it, and it is on its way to another form. */}
                {completion.allowAnother !== false && completion.mode !== 'redirect' && !completion.continueStraight
                  && !resp.oncePerBrowser && (
                  <button className="of-btn" onClick={resetForm}>{S.submitAnother}</button>
                )}
              </>
            ) : (
              <>
                <h2>{result.duplicate ? S.duplicate.title : result.testExpired ? 'Test link expired' : 'Submission Error'}</h2>
                <p>{result.error}</p>
                {!result.duplicate && !result.testExpired && (
                  <button className="of-btn" onClick={() => setResult(null)}>Try Again</button>
                )}
                {result.testExpired && (
                  <a
                    className="of-btn" style={{ textDecoration: 'none' }}
                    href={resp.test && !('expired' in resp.test) ? `/admin/forms/${resp.test.formId}/build` : '/admin'}
                  >
                    ← Back to editor
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <form
        className="of-container"
        // The engine's validator reports every problem under its control and
        // in the summary; the browser's own bubbles would only compete with it.
        noValidate
        // On a paged form, an implicit submit (Enter in a field) before the
        // last page advances instead of submitting.
        onSubmit={(e) => {
          if (pager.paged && !pager.atLast) { e.preventDefault(); pager.next(); return; }
          void onSubmit(e);
        }}
      >
        {submitClosed && <ClosedNotice reason={submitClosed.closed} schedule={resp.form.settings.schedule} contact={submitClosed.contact} inline />}
        {edit && (
          <div className="of-resume" role="status">
            <div className="of-resume-text">
              <b>{S.edit.banner}</b>{' '}
              {S.edit.until(new Date(edit.expiresAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }))}
            </div>
          </div>
        )}
        {editFailed && <div className="of-gate-error" style={{ marginBottom: 16 }}>{S.edit.expired}</div>}
        {resumeOffer && (
          <div className="of-resume" role="status">
            <div className="of-resume-text">
              <b>Pick up where you left off?</b>{' '}
              You have unsent answers from {new Date(resumeOffer.savedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}.
            </div>
            <div className="of-resume-actions">
              <button type="button" className="of-btn of-resume-btn" onClick={onResume}>Resume</button>
              <button type="button" className="of-logout" onClick={onStartOver}>Start over</button>
            </div>
          </div>
        )}
        {logo}
        <div className="of-header">
          <div>
            <h1>{theme.headerTitle || resp.form.name}</h1>
            {theme.headerSubtitle && <p className="of-subtitle">{theme.headerSubtitle}</p>}
          </div>
          {resp.identity && (
            <div className="of-who">
              <div className="of-who-label">
                {resp.identity.providerName ? `Signed in with ${resp.identity.providerName}` : 'Email confirmed'}
              </div>
              <div className="of-who-email">{resp.identity.email || resp.identity.name}</div>
              <button
                type="button" className="of-logout"
                onClick={async () => { await signOutOfForm(formPath).catch(() => undefined); load(); }}
              >
                Sign out
              </button>
            </div>
          )}
        </div>

        {resp.payments && !resp.payments.ready && (
          <div className="of-gate-error" style={{ marginBottom: 16 }}>
            This form includes a payment step that is not fully set up yet. A submission that requires a payment will not go through — contact the form owner.
          </div>
        )}

        {/* Why some answers are already filled in. Without it the carried block
            reads as a form that has mysteriously guessed who you are. */}
        {resp.confirmPrompt && (
          <p className="of-info" style={{ marginBottom: 16 }}>{resp.confirmPrompt}</p>
        )}

        <PagerProgress pager={pager} />
        <ErrorSummaryTop pager={pager} />

        <FormRenderer
          def={def}
          state={state}
          onState={(update) => { setDirty(true); setState((s) => (s ? update(s) : s)); }}
          cfg={EMPTY_CFG}
          formName={resp.form.name}
          idPrefix="pf"
          sections={pager.paged ? pager.sections : undefined}
          errors={pager.errors}
          onClearError={pager.clearError}
          onFieldError={pager.setError}
          uploads={settings.uploads}
          requiredStyle={settings.requiredStyle}
          carried={resp.carried
            ? {
              fields: resp.carried,
              confirmations,
              onConfirm: (key, correct) => setConfirmations((c) => ({ ...c, [key]: correct })),
              showErrors: showConfirmErrors,
            }
            : undefined}
          accountLocked={edit
            ? (edit.lockedFields.length ? { fields: edit.lockedFields, reason: 'locked' } : undefined)
            : accountLocked.length
              ? { fields: accountLocked, providerName: resp.identity?.providerName }
              : undefined}
          provided={edit?.provided}
          link={resp.softPrefill?.length || resp.linkLocked?.length
            ? { locked: resp.linkLocked || [], prefilled: resp.softPrefill || [] }
            : undefined}
        />

        {/* Honeypot — off-screen and skipped by keyboard/screen readers; a
            submission that fills it in did not come from a person. */}
        <div aria-hidden="true" style={{ position: 'absolute', left: -9999, top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
          <label htmlFor="pf-website-url">Leave this field empty</label>
          <input
            id="pf-website-url" type="text" tabIndex={-1} autoComplete="off"
            value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        {/* The captcha (U6), on the page that submits. */}
        {resp.captcha && (!pager.paged || pager.atLast) && (
          <CaptchaField
            siteKey={resp.captcha.siteKey}
            resetKey={captchaReset}
            error={pager.errors[CAPTCHA_PATH]}
            onToken={(t) => { setCaptchaToken(t); if (t && !captchaRefused.current) pager.clearError(CAPTCHA_PATH); }}
          />
        )}

        <PagerControls pager={pager}>
          <button type="submit" className="of-btn" disabled={submitting}>
            {edit ? S.edit.submit : settings.submitLabel || S.submit}
          </button>
          {/* "Save and continue later" (P6): under Submit, when this server can email the link. */}
          {resp.resume && !edit && (
            <button type="button" className="of-linkbtn" onClick={openLater}>{S.resume.link}</button>
          )}
        </PagerControls>
      </form>
      {/* The thank-you and payment screens are full-screen overlays, so the
          badge has to sit on top of one rather than behind it — and a
          respondent who has just finished a form is exactly who it is for. */}
      {result || payReturn ? gateBadge : badge}
    </div>
  );
}
